import React, { useState, useEffect } from 'react';
import { Save, ArrowRight, ArrowLeft, Wand2, Copy, Check, MessageSquareWarning, RefreshCw, Loader2, Split, Eye, ChevronRight, Globe, AlertTriangle, Languages, ArrowUpRight } from 'lucide-react';
import StageIndicator from '../components/StageIndicator';
import { Story, Language, StoryStatus, SocialPosts } from '../types';
import { createEmptyStory, saveStory, getStory, createStoryFromTranslation } from '../services/store';
import { MOCK_USER, LANGUAGE_LABELS, isRTL } from '../constants';
import { runJournalistAgent, runCriticAgent, runSocialAgent, suggestHeadline, runTranslatorAgent } from '../services/geminiService';
import { diffWords, getDiffStats } from '../utils/diff';
import { supabase } from '../services/supabaseClient';

interface NewStoryProps {
  storyId?: string;
  onExit: () => void;
}

const NewStory: React.FC<NewStoryProps> = ({ storyId, onExit }) => {
  // State
  const [story, setStory] = useState<Story | null>(null);
  const [stage, setStage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  
  // Alert State
  const [showLanguageAlert, setShowLanguageAlert] = useState<string | null>(null);
  const [showUnappliedFeedbackAlert, setShowUnappliedFeedbackAlert] = useState(false);
  
  // Actor-Critic State
  const [criticIssues, setCriticIssues] = useState<string[]>([]);
  const [customIssue, setCustomIssue] = useState("");
  
  // UI State
  const [showDiff, setShowDiff] = useState(false);
  
  // Initialization
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        onExit(); 
        return;
      }

      if (storyId) {
        const existing = await getStory(storyId);
        if (existing) {
          setStory(existing);
          // CRITICAL FIX: Ensure correct stage restoration based on status
          if (existing.status === StoryStatus.PUBLISHED || existing.status === StoryStatus.STAGE5) {
            setStage(4); // Go to Social/Final
          } else if (existing.status === StoryStatus.STAGE2) {
            setStage(2); // Go to Review
          } else {
            setStage(1); // Default to Write
          }
          
          if (existing.editorIssues) setCriticIssues(existing.editorIssues);
        }
      } else {
        const newStory = createEmptyStory(user.id);
        setStory(newStory);
      }
    }
    init();
  }, [storyId]);

  if (!story) return <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-slate-950"><Loader2 className="animate-spin h-8 w-8 text-blue-600 dark:text-blue-400"/></div>;

  const handleSaveAndExit = async () => {
    if (story) await saveStory(story);
    onExit();
  };

  const updateStory = async (updates: Partial<Story>) => {
    setStory(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updates, updatedAt: new Date().toISOString() };
      // Save in background to not block UI, but in a real app might want a 'saved' indicator
      saveStory(updated);
      return updated;
    });
  };

  // --- Helpers ---
  const detectLanguageMismatch = (text: string, currentLang: Language): string | null => {
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/g;
    const latinRegex = /[a-zA-Z]/g;
    
    // Sample first 500 chars for efficiency
    const sample = text.slice(0, 500);
    const arabicCount = (sample.match(arabicRegex) || []).length;
    const latinCount = (sample.match(latinRegex) || []).length;
    
    if (sample.length < 10) return null;

    if (currentLang === Language.ENGLISH) {
      if (arabicCount > latinCount) {
        return "It looks like you're writing in Dari or Pashto, but 'English' is selected.";
      }
    } else {
      // Dari or Pashto
      if (latinCount > arabicCount) {
        return "It looks like you're writing in English, but 'Dari/Pashto' is selected.";
      }
    }
    return null;
  };

  // --- Actions ---

  const handleSuggestHeadline = async () => {
    if (!story.body || story.body.length < 50) return;
    setIsLoading(true);
    setLoadingMsg("AI is reading your story...");
    try {
      const suggestion = await suggestHeadline(story.body, story.language);
      updateStory({ headline: suggestion.headline, deck: suggestion.deck });
    } finally {
      setIsLoading(false);
    }
  };

  const attemptSubmitForReview = () => {
    // Language Check
    const mismatchWarning = detectLanguageMismatch(story.body, story.language);
    if (mismatchWarning) {
      setShowLanguageAlert(mismatchWarning);
      return;
    }
    executeSubmitForReview();
  }

  const executeSubmitForReview = async () => {
    setShowLanguageAlert(null); // Clear alert if open
    setIsLoading(true);
    setLoadingMsg("Agent 1 (Journalist) is polishing...");
    
    // Create temp copy for atomic updates
    let currentStory = { ...story };

    if (!story.originalBody) {
      currentStory = { 
        ...currentStory,
        originalBody: story.body, 
        originalHeadline: story.headline, 
        originalDeck: story.deck, 
        status: StoryStatus.STAGE2 
      };
      await updateStory(currentStory);
    }

    try {
      const polished = await runJournalistAgent(currentStory.headline, currentStory.deck, currentStory.body, currentStory.language);
      
      setLoadingMsg("Agent 2 (Critic) is reviewing...");
      
      // Pass isTranslation flag so the Critic knows to only proofread, not structurally edit
      const critique = await runCriticAgent(polished.headline, polished.deck, polished.body, currentStory.language, currentStory.isTranslation);

      await updateStory({ 
        headline: polished.headline, 
        deck: polished.deck, 
        body: polished.body, 
        editorIssues: critique.issues
      });
      setCriticIssues(critique.issues);
      setStage(2);
    } catch (e) {
      alert("AI Review Failed. Please try again.");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFeedback = async () => {
    if (criticIssues.length === 0) return;
    setIsLoading(true);
    setLoadingMsg("Agent 1 is applying feedback...");

    try {
      const polished = await runJournalistAgent(story.headline, story.deck, story.body, story.language, criticIssues);
      await updateStory({
        headline: polished.headline,
        deck: polished.deck,
        body: polished.body,
        editorIssues: [] 
      });
      setCriticIssues([]); 
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Safe finish review check
  const handleFinishReview = () => {
    if (criticIssues.length > 0) {
      setShowUnappliedFeedbackAlert(true);
    } else {
      setStage(3);
    }
  };

  const handleGenerateSocial = async () => {
    setIsLoading(true);
    setLoadingMsg("Agent 3 (Social) is creating posts...");
    try {
      const posts = await runSocialAgent(story.headline, story.body, story.language);
      await updateStory({ socialPosts: posts, status: StoryStatus.STAGE5 });
      setStage(4);
    } catch(e) {
       console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTranslation = async (targetLang: Language) => {
    setIsLoading(true);
    setLoadingMsg(`Translating to ${LANGUAGE_LABELS[targetLang]}...`);
    
    try {
      // Use current finalized content for translation, not original input
      const translated = await runTranslatorAgent(story.headline, story.deck, story.body, story.language, targetLang);
      
      // Pass the CURRENT story object so it can link the new translation to the same group
      const newStory = await createStoryFromTranslation(story, translated, targetLang);
      
      // Update local state to switch to the new story
      setStory(newStory);
      setStage(1); // Reset to Write/Edit stage so user can review the translation
      
    } catch (e) {
      alert("Translation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Renders ---

  // Custom Modal for Language Alert
  const renderLanguageAlert = () => {
    if (!showLanguageAlert) return null;
    return (
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700 overflow-hidden transform transition-all scale-100">
          <div className="p-6">
            <div className="flex items-start space-x-4">
              <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Language Mismatch Detected</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {showLanguageAlert}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/50 px-6 py-4 flex justify-end space-x-3 border-t border-slate-100 dark:border-slate-800">
             <button 
               onClick={() => setShowLanguageAlert(null)}
               className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
             >
               Go Back & Fix
             </button>
             <button 
               onClick={executeSubmitForReview}
               className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
             >
               Proceed Anyway
             </button>
          </div>
        </div>
      </div>
    );
  };

  // Custom Modal for Unapplied Feedback Alert
  const renderFeedbackAlert = () => {
    if (!showUnappliedFeedbackAlert) return null;
    return (
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700 overflow-hidden transform transition-all scale-100">
          <div className="p-6">
            <div className="flex items-start space-x-4">
              <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-full">
                <MessageSquareWarning className="h-6 w-6 text-orange-600 dark:text-orange-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Unapplied Feedback</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  There are still {criticIssues.length} items in the Editor's Notes. Proceeding now will skip applying these changes.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/50 px-6 py-4 flex justify-end space-x-3 border-t border-slate-100 dark:border-slate-800">
             <button 
               onClick={() => setShowUnappliedFeedbackAlert(false)}
               className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
             >
               Go Back
             </button>
             <button 
               onClick={() => {
                 setShowUnappliedFeedbackAlert(false);
                 setStage(3);
               }}
               className="px-4 py-2 text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 rounded-lg shadow-sm transition-colors"
             >
               Skip & Finish
             </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStage1 = () => {
    const isRtl = isRTL(story.language);
    return (
      <div className="flex flex-col h-full bg-white dark:bg-slate-950 animate-in fade-in duration-300 relative">
        {renderLanguageAlert()}
        
        {/* Editor Toolbar */}
        <div className="flex-shrink-0 px-8 py-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between transition-colors">
          <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 tracking-wider uppercase">
            Story Editor {story.isTranslation && <span className="ml-2 bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] dark:bg-indigo-900/30 dark:text-indigo-400">Translation</span>}
          </div>
          <button 
            onClick={handleSuggestHeadline}
            disabled={!story.body || story.body.length < 50 || isLoading}
            className="text-blue-600 dark:text-blue-400 text-xs font-medium hover:text-blue-800 dark:hover:text-blue-300 disabled:text-slate-300 dark:disabled:text-slate-700 flex items-center px-3 py-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            <Wand2 className="h-3 w-3 mr-1.5"/> Suggest Headline
          </button>
        </div>

        {/* Main Editor Canvas */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">
            <input
              type="text"
              dir={isRtl ? 'rtl' : 'ltr'}
              value={story.headline}
              onChange={e => updateStory({ headline: e.target.value })}
              className={`w-full text-4xl font-bold text-slate-900 dark:text-white placeholder-slate-300 dark:placeholder-slate-700 border-none p-0 focus:ring-0 bg-transparent ${isRtl ? 'font-arabic' : ''}`}
              placeholder="Headline"
            />
            
            <textarea
              dir={isRtl ? 'rtl' : 'ltr'}
              value={story.deck}
              onChange={e => updateStory({ deck: e.target.value })}
              rows={1}
              className={`w-full text-xl text-slate-500 dark:text-slate-400 italic placeholder-slate-300 dark:placeholder-slate-700 border-none p-0 focus:ring-0 resize-none overflow-hidden bg-transparent ${isRtl ? 'font-arabic' : ''}`}
              placeholder="Summary / Deck..."
              style={{ minHeight: '3rem' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = target.scrollHeight + 'px';
              }}
            />

            <div className="w-full h-px bg-slate-100 dark:bg-slate-800 my-4" />

            <textarea
              dir={isRtl ? 'rtl' : 'ltr'}
              value={story.body}
              onChange={e => updateStory({ body: e.target.value })}
              className={`w-full min-h-[500px] text-lg leading-relaxed text-slate-800 dark:text-slate-200 placeholder-slate-200 dark:placeholder-slate-800 border-none p-0 focus:ring-0 resize-none bg-transparent ${isRtl ? 'font-arabic' : ''}`}
              placeholder="Start writing your story..."
            />
          </div>
        </div>

        {/* Footer Action Bar */}
        <div className="flex-shrink-0 px-8 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center transition-colors">
           <div className="text-xs text-slate-400 dark:text-slate-500 font-medium">
             {story.body?.length || 0} characters
           </div>
           <div className="flex space-x-4">
              <button
                onClick={attemptSubmitForReview}
                disabled={!story.body || story.body.length < 50 || isLoading}
                className="flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-lg disabled:bg-slate-300 dark:disabled:bg-slate-800 disabled:cursor-not-allowed transition-all font-medium text-sm shadow-sm"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="flex items-center">Submit for AI Review <ArrowRight className="ml-2 h-4 w-4"/></span>}
              </button>
           </div>
        </div>
      </div>
    );
  };

  const renderStage2 = () => {
    const isRtl = isRTL(story.language);
    const bodyDiffs = showDiff ? diffWords(story.originalBody || '', story.body) : [];
    const stats = showDiff ? getDiffStats(bodyDiffs) : { added: 0, removed: 0 };
    
    return (
      <div className="flex flex-col h-full animate-in fade-in duration-300 relative">
        {renderFeedbackAlert()}
        
        <div className="flex-1 flex overflow-hidden">
          {/* Main Content Area */}
          <div className="flex-1 flex flex-col bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 transition-colors">
            <div className="flex-shrink-0 bg-white dark:bg-slate-900 px-6 py-3 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center z-10">
              <div className="flex items-center">
                 {showDiff ? <Split className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" /> : <Wand2 className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400"/>}
                 <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                   {showDiff ? "Comparison View" : "Polished Draft"}
                 </h3>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowDiff(!showDiff)}
                  className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors border ${
                    showDiff 
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' 
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {showDiff ? "Hide Changes" : "Show Changes"}
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-8 relative">
               {showDiff && (
                  <div className="fixed top-32 right-1/3 mr-8 text-xs text-slate-500 dark:text-slate-400 font-medium bg-white/90 dark:bg-slate-800/90 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm z-10 pointer-events-none backdrop-blur-sm">
                     <span className="text-green-600 dark:text-green-400">+{stats.added}</span> / <span className="text-red-500 dark:text-red-400">-{stats.removed} words</span>
                  </div>
               )}
               
               <div className="max-w-3xl mx-auto space-y-6">
                 <input
                    type="text"
                    dir={isRtl ? 'rtl' : 'ltr'}
                    value={story.headline}
                    onChange={e => updateStory({ headline: e.target.value })}
                    disabled={showDiff}
                    className={`w-full text-2xl font-bold border-none p-0 focus:ring-0 text-slate-900 dark:text-white placeholder-slate-300 dark:placeholder-slate-700 bg-transparent ${isRtl ? 'font-arabic' : ''} ${showDiff ? 'cursor-default' : ''}`}
                  />
                  <textarea
                    dir={isRtl ? 'rtl' : 'ltr'}
                    value={story.deck}
                    onChange={e => updateStory({ deck: e.target.value })}
                    rows={2}
                    disabled={showDiff}
                    className={`w-full text-lg text-slate-600 dark:text-slate-400 border-none p-0 focus:ring-0 resize-none italic bg-transparent ${isRtl ? 'font-arabic' : ''} ${showDiff ? 'cursor-default' : ''}`}
                  />
                  
                  {showDiff ? (
                     <div className={`text-lg leading-relaxed whitespace-pre-wrap dark:text-slate-300 ${isRtl ? 'font-arabic text-right' : ''}`}>
                        {bodyDiffs.map((part, i) => {
                          if (part.added) return <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded box-decoration-clone">{part.value}</span>;
                          if (part.removed) return <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 dark:text-red-300 line-through decoration-red-400/50 px-0.5 opacity-60 box-decoration-clone">{part.value}</span>;
                          return <span key={i} className="text-slate-800 dark:text-slate-200">{part.value} </span>;
                        })}
                     </div>
                  ) : (
                    <textarea
                      dir={isRtl ? 'rtl' : 'ltr'}
                      value={story.body}
                      onChange={e => updateStory({ body: e.target.value })}
                      className={`w-full min-h-[600px] text-lg leading-relaxed text-slate-800 dark:text-slate-200 border-none p-0 focus:ring-0 resize-none bg-transparent ${isRtl ? 'font-arabic' : ''}`}
                    />
                  )}
               </div>
            </div>
          </div>

          {/* Issues Sidebar (Critic Agent) */}
          <div className="w-80 flex-shrink-0 flex flex-col bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 transition-colors">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-800/50">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm flex items-center">
                <MessageSquareWarning className="h-4 w-4 mr-2 text-orange-500"/> Editor's Notes
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {criticIssues.length === 0 ? (
                <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                  <Check className="h-8 w-8 mx-auto text-green-500 mb-2"/>
                  <p className="text-sm">No issues found!</p>
                </div>
              ) : (
                criticIssues.map((issue, idx) => (
                  <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm flex items-start group hover:border-orange-200 dark:hover:border-orange-800/50 transition-colors">
                    <span className="text-xs font-bold text-slate-300 dark:text-slate-600 mr-2 mt-0.5">{idx + 1}</span>
                    <p className="text-sm text-slate-700 dark:text-slate-300 flex-1 leading-snug">{issue}</p>
                    <button 
                      onClick={() => setCriticIssues(prev => prev.filter((_, i) => i !== idx))}
                      className="text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
              
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                 <textarea 
                   value={customIssue}
                   onChange={e => setCustomIssue(e.target.value)}
                   className="w-full text-sm p-2 border rounded-md mb-2 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                   placeholder="Add your own note..."
                   rows={2}
                 />
                 <button 
                   onClick={() => {
                     if (customIssue.trim()) {
                       setCriticIssues([...criticIssues, customIssue.trim()]);
                       setCustomIssue("");
                     }
                   }}
                   disabled={!customIssue.trim()}
                   className="w-full text-xs bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 py-1.5 rounded transition-colors"
                 >
                   Add Note
                 </button>
              </div>
            </div>

            <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={handleApplyFeedback}
                disabled={criticIssues.length === 0 || isLoading}
                className="w-full flex justify-center items-center py-2.5 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin"/> : <><RefreshCw className="h-4 w-4 mr-2"/> Apply Feedback</>}
              </button>
              
              <button
                onClick={handleFinishReview}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
              >
                Finish Review &rarr;
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStage3 = () => {
     const isRtl = isRTL(story.language);
     return (
       <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950 animate-in fade-in duration-300">
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 p-10 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
              <div className={`prose dark:prose-invert max-w-none ${isRtl ? 'font-arabic text-right' : ''}`}>
                 <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">{story.headline}</h1>
                 <div className="text-xl text-slate-600 dark:text-slate-300 mb-8 italic border-l-4 border-blue-500 pl-4 py-1">{story.deck}</div>
                 <div className="whitespace-pre-wrap text-slate-800 dark:text-slate-200 leading-relaxed text-lg">{story.body}</div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-8 py-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center transition-colors">
            <button onClick={() => setStage(2)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium flex items-center transition-colors">
              <ArrowLeft className="h-4 w-4 mr-1"/> Back to Edit
            </button>
            <div className="flex space-x-3">
              <button 
                onClick={() => {
                   const text = `${story.headline}\n\n${story.deck}\n\n${story.body}`;
                   navigator.clipboard.writeText(text);
                   alert("Copied to clipboard!");
                }}
                className="flex items-center px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors"
              >
                <Copy className="h-4 w-4 mr-2"/> Copy Text
              </button>
              <button 
                onClick={handleGenerateSocial}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
              >
                Generate Social Media <ArrowRight className="h-4 w-4 ml-2"/>
              </button>
            </div>
          </div>
       </div>
     );
  };

  const renderStage4 = () => {
    // Determine which languages we can translate TO
    const availableTranslations = Object.values(Language).filter(l => l !== story.language);
    
    return (
      <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950 animate-in fade-in duration-300">
        <div className="flex-1 overflow-y-auto p-8">
           <div className="max-w-6xl mx-auto space-y-8">
             
             {/* Social Media Section */}
             <div>
               <div className="flex justify-between items-center mb-6">
                  <div>
                     <h2 className="text-xl font-bold text-slate-900 dark:text-white">Social Media Posts</h2>
                     <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Ready-to-post content generated for your story.</p>
                  </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {story.socialPosts && Object.entries(story.socialPosts).map(([platform, lengths]) => (
                    <div key={platform} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-full transition-colors">
                       <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                         <span className="capitalize font-bold text-slate-700 dark:text-slate-200">{platform}</span>
                         <span className="text-xs text-slate-400 dark:text-slate-500 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded-full capitalize">{platform}</span>
                       </div>
                       <div className="p-5 space-y-6 flex-1">
                          {Object.entries(lengths).map(([len, text]) => (
                             <div key={len} className="space-y-1.5 group">
                                <div className="flex justify-between items-center">
                                   <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 tracking-wider">{len} Version</span>
                                   <button 
                                     onClick={() => navigator.clipboard.writeText(text as string)}
                                     className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity flex items-center"
                                    >
                                     <Copy className="h-3 w-3 mr-1"/> Copy
                                   </button>
                                </div>
                                <div className={`p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-800 dark:text-slate-200 border border-slate-100 dark:border-slate-700 group-hover:border-blue-200 dark:group-hover:border-blue-800 transition-colors ${isRTL(story.language) ? 'font-arabic text-right' : ''}`}>
                                  {text as string}
                                </div>
                             </div>
                          ))}
                       </div>
                    </div>
                  ))}
               </div>
             </div>

             {/* Translation Desk */}
             <div className="pt-8 border-t border-slate-200 dark:border-slate-800">
               <div className="flex items-center mb-6">
                 <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg mr-3">
                   <Languages className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                 </div>
                 <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Translation Desk</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Translate this finished story to start the workflow in another language.</p>
                 </div>
               </div>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {availableTranslations.map(lang => (
                   <button
                     key={lang}
                     onClick={() => handleCreateTranslation(lang)}
                     className="flex items-center p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md transition-all group text-left"
                   >
                     <div className="h-10 w-10 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mr-4 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 transition-colors">
                       <Globe className="h-5 w-5 text-slate-400 dark:text-slate-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
                     </div>
                     <div>
                       <span className="block font-semibold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                         Translate to {LANGUAGE_LABELS[lang]}
                       </span>
                       <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          <span>Creates new draft in same group</span>
                          <ArrowUpRight className="h-3 w-3 ml-1 opacity-50" />
                       </div>
                     </div>
                     <ArrowRight className="h-5 w-5 ml-auto text-slate-300 dark:text-slate-600 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transform group-hover:translate-x-1 transition-all" />
                   </button>
                 ))}
               </div>
             </div>

           </div>
        </div>
        
        <div className="flex-shrink-0 px-8 py-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center transition-colors">
           <button onClick={() => setStage(3)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium flex items-center transition-colors">
              <ArrowLeft className="h-4 w-4 mr-1"/> Back to Article
           </button>
           <button onClick={handleSaveAndExit} className="flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg text-sm font-medium shadow-sm transition-colors">
              <Check className="h-4 w-4 mr-2"/> Done & Return to Queue
           </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-950 overflow-hidden transition-colors">
      {/* Slim Top Bar */}
      <div className="h-14 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 bg-white dark:bg-slate-900 flex-shrink-0 z-20 shadow-sm transition-colors">
        <div className="flex items-center">
          <button onClick={onExit} className="mr-4 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          
          <div className="flex items-center h-8 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 mr-6 transition-colors">
             <select 
               value={story.language}
               onChange={e => updateStory({ language: e.target.value as Language })}
               className="bg-transparent border-none text-xs font-semibold text-slate-700 dark:text-slate-300 focus:ring-0 cursor-pointer pl-2 pr-6 py-0 h-full"
             >
               {Object.entries(LANGUAGE_LABELS).map(([key, label]) => (
                 <option key={key} value={key} className="dark:bg-slate-800">{label}</option>
               ))}
             </select>
          </div>

          <StageIndicator currentStage={stage} />
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleSaveAndExit}
            className="text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white font-medium flex items-center px-3 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Save & Exit
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 z-50 flex flex-col items-center justify-center backdrop-blur-sm transition-colors">
             <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 flex flex-col items-center">
                <Loader2 className="h-10 w-10 text-blue-600 dark:text-blue-400 animate-spin mb-4"/>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 animate-pulse">{loadingMsg}</p>
             </div>
          </div>
        )}
        
        {stage === 1 && renderStage1()}
        {stage === 2 && renderStage2()}
        {stage === 3 && renderStage3()}
        {stage === 4 && renderStage4()}
      </div>
    </div>
  );
};

export default NewStory;